import React from 'react'
import Box from '@mui/material/Box';
import { Document, Page, pdfjs } from "react-pdf";
import './Viewer.css';
import { Button, Chip, CircularProgress, Divider, FormControl, LinearProgress, TextField, Typography } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { EditorTiny } from '../../../../components/EditorTiny'
// import "//unpkg.com/mathlive";
import 'mathlive';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Axios from 'axios';
import FileUpload from '../../../../components/FileUpload';
import GradeModal from './GradeModal';
import QuestionRender from '../../../../components/QuestionRender';
import { CoordinateGridStudent, CoordinateGridReadOnly, CoordinateGrid, defaultCoordinateConfig, formatElementSummary, parseCoordinateAnswerForDisplay, AnswerLogicSummary } from '../../../../components/coordinateGrid';
import { TableStudent, TableReadOnly, TableAnswerLogicSummary, defaultTableConfig } from '../../../../components/tableAnswer';
import { NumberLineStudent, NumberLineReadOnly, defaultNumberLineConfig, getUnionAllowedModes as getUnionAllowedModesNumberLine } from '../../../../components/numberLine';
import { MappingStudent, MappingReadOnly, defaultMappingConfig } from '../../../../components/mappingAnswer';
import { PolynomialGraphStudent, PolynomialGraphReadOnly, defaultPolynomialGraphConfig } from '../../../../components/polynomialGraph';
import { RationalGraphStudent, RationalGraphReadOnly, defaultRationalGraphConfig } from '../../../../components/rationalGraph';
import { TrigGraphStudent, TrigGraphReadOnly, defaultTrigGraphConfig } from '../../../../components/trigGraph';
import { MultilineStudent, MultilineReadOnly, defaultMultilineConfig } from '../../../../components/multiline';
import {
    MATHLIVE_ANSWER_TYPES,
    parseMathliveAnswer,
    stringifyMathliveAnswer,
} from '../../../../components/mathlive';
import Dialog from '@mui/material/Dialog';
import { addCacheBuster } from '../../../../utils/cacheUtils';
import { BINARY_GRADING_TYPES } from '../../../../utils/hwConstants';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { getAuthHeader } from '../../../../utils/authToken';
import { useNavigate } from 'react-router-dom';
import RegradePreviewDialog, { canManageHomeworkGrades } from './RegradePreviewDialog';
import HomeworkReviewQueueDialog from './HomeworkReviewQueueDialog';
import { normalizeHomeworkAnswerForSave } from './homeworkAnswerState';

// Component for collapsible long answer display
const CollapsibleAnswer = ({ content, isHtml, colors, label = 'Student Answer' }) => {
    const [expanded, setExpanded] = React.useState(false);
    const MAX_HEIGHT = 150;
    const contentRef = React.useRef(null);
    const [needsCollapse, setNeedsCollapse] = React.useState(false);

    React.useEffect(() => {
        if (contentRef.current) {
            setNeedsCollapse(contentRef.current.scrollHeight > MAX_HEIGHT);
        }
    }, [content]);

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: colors.grey[300] }}>
                {label}:
            </Typography>
            <Box
                ref={contentRef}
                sx={{
                    p: 2,
                    backgroundColor: colors.primary[750],
                    borderRadius: '8px',
                    border: `1px solid ${colors.grey[700]}`,
                    maxHeight: expanded ? 'none' : `${MAX_HEIGHT}px`,
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'max-height 0.3s ease',
                    // Fade effect when collapsed
                    ...(!expanded && needsCollapse && {
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '50px',
                            background: `linear-gradient(transparent, ${colors.primary[750]})`,
                        },
                    }),
                }}
            >
                {isHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: content || '' }} />
                ) : (
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{content || ''}</Typography>
                )}
            </Box>
            {needsCollapse && (
                <Button
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                    startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mt: 1, textTransform: 'none' }}
                >
                    {expanded ? 'Show less' : 'Show more'}
                </Button>
            )}
        </Box>
    );
};

/**
 * Helper to parse answer data that may be in array or string format
 * @param {Array|string} data - Answer data (array with JSON string or direct JSON string)
 * @param {*} defaultValue - Default value if parsing fails
 * @returns {*} Parsed answer object or defaultValue
 */
const parseAnswerData = (data, defaultValue) => {
    try {
        const str = Array.isArray(data) ? data[0] : data;
        return str ? JSON.parse(str) : defaultValue;
    } catch {
        return defaultValue;
    }
};

/**
 * Extract correct answer from table answer_config
 * The correct values are stored in answer_config.rows[].cells[].value
 * @param {Object} config - Table answer_config
 * @returns {Object} Answer in format { rows: [{ id, cells: { colId: value } }] }
 */
const extractTableCorrectAnswer = (config) => {
    if (!config || !config.rows) return null;
    return {
        rows: config.rows.map(row => ({
            id: row.id,
            cells: Object.fromEntries(
                Object.entries(row.cells || {}).map(([colId, cell]) => [
                    colId,
                    cell?.value || ''
                ])
            )
        }))
    };
};

/**
 * Note: For mapping, correct connections are stored in question.answer, not answer_config.
 * This is a no-op fallback that returns empty string since we can't extract from config.
 */
const extractMappingCorrectAnswer = () => {
    return '';
};

// Helper to format coordinate answer for display
const formatCoordinateAnswerDisplay = (answer, config, _colors) => {
    const parsed = parseCoordinateAnswerForDisplay(answer, config, 150);
    if (!parsed) return null;

    const { elements, gridConfig, gridSize } = parsed;

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mt: 1 }}>
            <CoordinateGrid
                config={gridConfig}
                elements={elements}
                pendingPoints={[]}
                mode="point"
                readOnly={true}
                width={gridSize}
                height={gridSize}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {elements.map((el, i) => (
                    <Chip
                        key={el.id || i}
                        label={formatElementSummary(el)}
                        size="small"
                        sx={{ fontSize: '0.7rem' }}
                    />
                ))}
            </Box>
        </Box>
    );
};

/**
 * Infer allowedModes from the correct answer elements
 * This ensures students can draw the same element types as required in the answer
 * Note: 'equationLine' is treated as 'line' for mode purposes
 * Note: 'polynomial' curves use mode names like 'polynomial2', 'polynomial3', etc. based on degree
 */
const inferAllowedModesFromAnswer = (question) => {
    if (!question.answer || !question.answer[0]) return ['point'];

    try {
        const parsed = JSON.parse(question.answer[0]);
        if (!parsed.elements || !Array.isArray(parsed.elements)) return ['point'];

        // Map equationLine to line, and curve to its curveType for mode selection
        const elementTypes = [...new Set(parsed.elements.map(el => {
            if (el.type === 'equationLine') return 'line';
            if (el.type === 'curve') {
                // For polynomial curves, return mode name with degree (e.g., 'polynomial3')
                if (el.curveType === 'polynomial' && el.parsed?.degree) {
                    return `polynomial${el.parsed.degree}`;
                }
                return el.curveType; // quadratic, absolute, sqrt, etc.
            }
            return el.type;
        }))];
        return elementTypes.length > 0 ? elementTypes : ['point'];
    } catch (e) {
        return ['point'];
    }
};

/**
 * Combine admin-configured allowedModes with answer-inferred modes
 * Returns union of both arrays (deduped)
 */
const getUnionAllowedModes = (question) => {
    const configModes = question.answer_config?.allowedModes || [];
    const inferredModes = inferAllowedModesFromAnswer(question);

    // Union of both, deduped
    const union = [...new Set([...configModes, ...inferredModes])];

    // Fallback to ['point'] if empty
    return union.length > 0 ? union : ['point'];
};

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const TextbookViewer = ({
    PDF,
    pages,
    pageFrom,
    pageTo,
    hwQuestions,
    setHWAnswers,
    hwAnswers,
    userId,
    userRole,
    course_id,
    homework_id,
    colors,
    submissionId,
    isSubmitted,
    setHWAnswerFiles,
    hwAnswerFiles,
    setErrorAlertMessage,
    setErrorAlertOpen,
    setSuccessAlertOpen,
    setSuccessAlertMessage,
    setBackdropOpen,
    handleLogout,
    setUpdatedHWAnswers,
    isStudentView,
}) => {
    const showAsStudent = isStudentView || userRole === 'student';
    const canManageGrades = canManageHomeworkGrades(userRole, isStudentView);

    // Derive available pages from props - prefer pages array, fall back to range
    const availablePages = React.useMemo(() => {
        if (pages && pages.length > 0) {
            return [...pages].sort((a, b) => a - b);
        }
        // Backward compat: generate from range
        const pagesFromRange = [];
        for (let p = pageFrom; p <= pageTo; p++) {
            pagesFromRange.push(p);
        }
        return pagesFromRange;
    }, [pages, pageFrom, pageTo]);

    const [selectedPage, setSelectedPage] = React.useState(availablePages[0] || 1);
    const [loading, setLoading] = React.useState(false);
    const [pdfLoadProgress, setPdfLoadProgress] = React.useState(0);
    const [submitDialogOpen, setSubmitDialogOpen] = React.useState(false);
    const [regradePreview, setRegradePreview] = React.useState(null);
    const [applyingRegrade, setApplyingRegrade] = React.useState(false);
    const [reviewQueueOpen, setReviewQueueOpen] = React.useState(false);
    const [reviewQueueItems, setReviewQueueItems] = React.useState([]);
    const [loadingReviewQueue, setLoadingReviewQueue] = React.useState(false);
    const [resolvingReviewAnswerId, setResolvingReviewAnswerId] = React.useState(null);
    const navigate = useNavigate();

    // Memoize the cache-busted PDF URL to prevent reload on every re-render
    const pdfUrl = React.useMemo(() => addCacheBuster(PDF), [PDF]);

    // Memoize pdf.js options to prevent re-triggering Document load on every render.
    // disableAutoFetch stops pdf.js from pre-downloading all pages after the first loads.
    const pdfOptions = React.useMemo(() => ({
        disableAutoFetch: true,
        disableStream: false,
    }), []);

    // ─────────────────────────────────────────────────────────────────────────
    // Auto-save engine (students only)
    //
    // Debounced, dirty-only, silent persistence of in-progress answers. Shares
    // persistAnswers with the manual "Save for later" / Submit paths.
    //
    // Multi-tab / multi-device behaviour:
    //   - Tabs editing DIFFERENT questions never collide (writes are dirty-only, one
    //     row per question).
    //   - Two tabs editing the SAME question resolve last-write-wins; there is no
    //     per-answer version check (a deliberate scope decision for a single student
    //     editing their own homework). refresh-on-refocus reconciles a tab only after
    //     it has been hidden >= STALE_HIDDEN_MS, so two concurrently-visible tabs can
    //     still clobber the same question.
    //   - The server's is_submitted guard (409 -> reconcile) prevents any tab from
    //     overwriting an already-submitted homework.
    // ─────────────────────────────────────────────────────────────────────────
    const AUTOSAVE_DEBOUNCE_MS = 1500;      // save this long after the last edit
    const AUTOSAVE_MAX_WAIT_MS = 10000;     // ...but never wait longer than this while typing
    const STALE_HIDDEN_MS = 10 * 60 * 1000; // refresh a tab hidden at least this long on return
    const AUTOSAVE_MAX_RETRIES = 5;         // backoff retries after a transient save failure

    const isStudent = userRole === 'student';

    const [saveStatus, setSaveStatus] = React.useState('idle'); // idle|unsaved|saving|saved|error|offline

    const dirtyRef = React.useRef(new Set());   // question ids with unsaved edits
    const savingRef = React.useRef(false);      // a save request is in flight
    const savingPromiseRef = React.useRef(null); // the in-flight autosave promise (for manual save/submit to await)
    const staleRef = React.useRef(false);       // submitted elsewhere -> stop saving
    const hiddenAtRef = React.useRef(null);     // timestamp the tab last went hidden
    const debounceTimerRef = React.useRef(null);
    const maxWaitStartRef = React.useRef(null); // start of the current debounce window
    const retryTimerRef = React.useRef(null);   // backoff retry after a transient failure
    const retryCountRef = React.useRef(0);      // consecutive transient failures

    // Latest-value refs so timer/event callbacks never read stale closures.
    const hwAnswersRef = React.useRef(hwAnswers);
    const hwQuestionsRef = React.useRef(hwQuestions);
    const hwAnswerFilesRef = React.useRef(hwAnswerFiles);
    const submissionIdRef = React.useRef(submissionId);
    const isSubmittedRef = React.useRef(isSubmitted);
    React.useEffect(() => {
        hwAnswersRef.current = hwAnswers;
        hwQuestionsRef.current = hwQuestions;
        hwAnswerFilesRef.current = hwAnswerFiles;
        submissionIdRef.current = submissionId;
        isSubmittedRef.current = isSubmitted;
    });

    // Function refs so mount-time event listeners always call the latest logic.
    const scheduleRef = React.useRef(() => {});
    const flushRef = React.useRef(() => {});
    const refreshRef = React.useRef(() => {});
    const keepaliveRef = React.useRef(() => {});

    const cancelAutoSaveTimer = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        maxWaitStartRef.current = null;
    };

    const markDirty = (questionId) => {
        if (!isStudent || isSubmittedRef.current || staleRef.current) return;
        dirtyRef.current.add(String(questionId));
        setSaveStatus((s) => (s === 'saving' ? s : 'unsaved'));
        scheduleRef.current();
    };

    // Update a single answer in local state and mark it for auto-save.
    const commitAnswer = (questionId, answer) => {
        setHWAnswers((prev) => ({
            ...prev,
            [questionId]: {
                ...prev[questionId],
                answer,
            },
        }));
        markDirty(questionId);
    };

    // Wrap MathLive plain-math answers in \(...\) delimiters (mirrors the manual save).
    const wrapMathLiveAnswer = (answer) => {
        if (!answer) return answer;
        const trimmed = answer.trim();
        if (/^\\\(.*\\\)$/.test(trimmed) || /^\$.*\$$/.test(trimmed) || /^\\\[.*\\\]$/.test(trimmed)) {
            return answer;
        }
        return `\\(${answer}\\)`;
    };

    // Build the server payload for a set of question keys (null = all answers).
    const buildAnswersPayload = (questionIds = null) => {
        const answersMap = hwAnswersRef.current || {};
        const questions = hwQuestionsRef.current || [];
        const keys = (questionIds || Object.keys(answersMap)).filter(
            (key) => answersMap[key] && answersMap[key].id != null
        );
        return keys.map((key) => {
            const question = questions.find((q) => String(q.id) === String(key));
            const isMathLive = question?.answer_tool === 'mathlive';
            // Mathlive with text-alternative stores a {type,value} JSON envelope;
            // LaTeX-wrapping would corrupt the JSON.
            const mathliveAllowsText = isMathLive && question?.answer_config?.allowTextInput === true;
            const normalizedAnswer = normalizeHomeworkAnswerForSave(question, answersMap[key].answer);
            return {
                id: answersMap[key].id,
                answer: (isMathLive && !mathliveAllowsText && normalizedAnswer !== null)
                    ? wrapMathLiveAnswer(normalizedAnswer)
                    : normalizedAnswer,
                hw_question_id: key,
            };
        });
    };

    // Shared persistence for manual save, submit, and auto-save.
    //   questionIds : null saves every answer; an array saves only those ids.
    //   includeFiles: also upload answer files (manual / submit only).
    //   feedback    : show backdrop + success snackbar (manual save only).
    //   autosave    : silent path; tags the request so the server skips the grading sweep.
    const persistAnswers = async ({ questionIds = null, includeFiles = false, feedback = false, autosave = false } = {}) => {
        if (userRole !== 'student') return false;

        const payload = buildAnswersPayload(questionIds);

        if (feedback) setBackdropOpen(true);

        // 1. Persist answers
        if (payload.length > 0) {
            try {
                await Axios.put(
                    `${process.env.REACT_APP_URL}/api/v1/hw-answers/save?user_id=${userId}${autosave ? '&autosave=1' : ''}`,
                    { answers: payload },
                    { headers: { Authorization: getAuthHeader() } }
                );
            } catch (error) {
                if (error.response?.status === 401) {
                    handleLogout();
                    return false;
                }
                if (error.response?.status === 409) {
                    // Submitted from another tab/device: stop saving and reconcile.
                    staleRef.current = true;
                    if (!autosave) {
                        setErrorAlertMessage('This homework has already been submitted.');
                        setErrorAlertOpen(true);
                    }
                    if (feedback) setBackdropOpen(false);
                    refreshRef.current();
                    return false;
                }
                if (!autosave) {
                    setErrorAlertMessage(error.response?.data?.message || 'Something went wrong');
                    setErrorAlertOpen(true);
                    if (feedback) setBackdropOpen(false);
                }
                return false;
            }
        }

        // 2. Persist answer files (manual save / submit only)
        if (includeFiles) {
            const files = hwAnswerFilesRef.current || {};
            const promises = [];
            Object.keys(files).forEach((key) => {
                const new_file = files[key]?.file || null;
                const prev_file = files[key]?.prev_file || null;
                if (!new_file || !key) return;
                const formData = new FormData();
                formData.append('file', new_file);
                formData.append('prev_file_id', prev_file ? prev_file.id : null);
                formData.append('hw_question_id', key);
                formData.append('hw_submission_id', submissionIdRef.current);
                promises.push(
                    Axios.post(
                        `${process.env.REACT_APP_URL}/api/v1/hw-answer-files/save-file?user_id=${userId}`,
                        formData,
                        { headers: { Authorization: getAuthHeader() } }
                    )
                );
            });
            if (promises.length > 0) {
                try {
                    await Promise.all(promises);
                } catch (error) {
                    if (error.response?.status === 401) {
                        handleLogout();
                        return false;
                    }
                    if (!autosave) {
                        setErrorAlertMessage(error.response?.data?.message || 'Something went wrong');
                        setErrorAlertOpen(true);
                        if (feedback) setBackdropOpen(false);
                    }
                    return false;
                }
            }
        }

        if (feedback) {
            setSuccessAlertMessage('Your answers saved successfully!');
            setSuccessAlertOpen(true);
            setBackdropOpen(false);
        }
        return true;
    };

    const scheduleAutoSave = () => {
        if (!isStudent || isSubmittedRef.current || staleRef.current) return;
        const now = Date.now();
        if (maxWaitStartRef.current === null) maxWaitStartRef.current = now;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        const elapsed = now - maxWaitStartRef.current;
        const wait = Math.min(AUTOSAVE_DEBOUNCE_MS, Math.max(0, AUTOSAVE_MAX_WAIT_MS - elapsed));
        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            maxWaitStartRef.current = null;
            flushRef.current();
        }, wait);
    };

    const flushAutoSave = async () => {
        cancelAutoSaveTimer();
        if (!isStudent || isSubmittedRef.current) {
            dirtyRef.current.clear();
            return;
        }
        if (staleRef.current) return;
        if (dirtyRef.current.size === 0) return;
        if (savingRef.current) return; // in-flight save; its tail reschedules any leftovers

        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            setSaveStatus('offline');
            return; // keep dirty; retried on the 'online' event
        }

        const ids = Array.from(dirtyRef.current);
        dirtyRef.current.clear();
        savingRef.current = true;
        setSaveStatus('saving');

        let ok = false;
        const savePromise = persistAnswers({ questionIds: ids, includeFiles: false, feedback: false, autosave: true });
        savingPromiseRef.current = savePromise;
        try {
            ok = await savePromise;
        } finally {
            savingRef.current = false;
            savingPromiseRef.current = null;
        }

        if (ok) {
            retryCountRef.current = 0;
            if (dirtyRef.current.size > 0) {
                // Edits arrived mid-flight: save again.
                setSaveStatus('unsaved');
                scheduleRef.current();
            } else {
                setSaveStatus('saved');
            }
        } else if (staleRef.current) {
            // Submitted elsewhere: refresh already requested, nothing to retry.
        } else {
            // Transient failure: keep the edits and retry.
            ids.forEach((id) => dirtyRef.current.add(id));
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                setSaveStatus('offline'); // the 'online' handler will retry
            } else {
                setSaveStatus('error');
                // Self-schedule a backoff retry (2s, 4s, 8s, 16s, 30s) so a student who
                // stopped typing still gets their last edit saved, honoring the chip's
                // "will retry" promise. After the cap, retry on next edit/refocus/reconnect.
                if (retryCountRef.current < AUTOSAVE_MAX_RETRIES) {
                    retryCountRef.current += 1;
                    const delay = Math.min(30000, 1000 * Math.pow(2, retryCountRef.current));
                    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
                    retryTimerRef.current = setTimeout(() => {
                        retryTimerRef.current = null;
                        flushRef.current();
                    }, delay);
                }
            }
        }
    };

    // Reliable last-gasp save on real page unload (pagehide/beforeunload). An async
    // XHR is aborted during teardown, so use fetch keepalive, which the browser is
    // allowed to finish after the page is gone. Fire-and-forget; ignores the in-flight
    // guard (send the current dirty set regardless) and clears dirty optimistically.
    const flushViaKeepalive = () => {
        if (!isStudent || isSubmittedRef.current || staleRef.current) return;
        if (dirtyRef.current.size === 0) return;
        const payload = buildAnswersPayload(Array.from(dirtyRef.current));
        if (payload.length === 0) return;
        try {
            fetch(`${process.env.REACT_APP_URL}/api/v1/hw-answers/save?user_id=${userId}&autosave=1`, {
                method: 'PUT',
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: getAuthHeader(),
                },
                body: JSON.stringify({ answers: payload }),
            });
            dirtyRef.current.clear();
        } catch (e) {
            // Nothing more we can do while the page is unloading.
        }
    };

    // Pull the latest server state (soft re-fetch via the parent) and reset local edit state.
    const requestRefresh = () => {
        cancelAutoSaveTimer();
        dirtyRef.current.clear();
        setSaveStatus('idle');
        setUpdatedHWAnswers(true);
    };

    // Keep the function refs pointed at the latest closures every render.
    scheduleRef.current = scheduleAutoSave;
    flushRef.current = flushAutoSave;
    refreshRef.current = requestRefresh;
    keepaliveRef.current = flushViaKeepalive;

    // Flush-on-hide, refresh-on-refocus, retry-on-reconnect (students only).
    React.useEffect(() => {
        if (!isStudent) return undefined;

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                hiddenAtRef.current = Date.now();
                if (dirtyRef.current.size > 0) flushRef.current();
            } else if (document.visibilityState === 'visible') {
                const hiddenMs = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : 0;
                hiddenAtRef.current = null;
                if (isSubmittedRef.current || staleRef.current) return;
                if (hiddenMs >= STALE_HIDDEN_MS) {
                    // Long absence: keep unsaved work if any, otherwise pull the latest.
                    if (dirtyRef.current.size > 0) flushRef.current();
                    else refreshRef.current();
                }
            }
        };

        const onOnline = () => {
            if (isSubmittedRef.current || staleRef.current) return;
            if (dirtyRef.current.size > 0) flushRef.current();
        };

        const onPageHide = () => {
            // Real unload: use keepalive so the request survives page teardown.
            keepaliveRef.current();
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('online', onOnline);
        window.addEventListener('pagehide', onPageHide);
        window.addEventListener('beforeunload', onPageHide);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('online', onOnline);
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('beforeunload', onPageHide);
            cancelAutoSaveTimer();
            flushRef.current(); // best-effort flush of any unsaved edits on unmount (no-ops when clean)
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStudent]);

    const handleClickDialogOpen = () => {
        if (userRole !== 'student') {
            setErrorAlertMessage('Only students can submit homework');
            setErrorAlertOpen(true);
            return;
        }
        setSubmitDialogOpen(true);
    };
  
    const handleDialogClose = () => {
        setSubmitDialogOpen(false);
    };
  
    if (isSubmitted) {
        const mathfields = document.querySelectorAll('math-field');
        // add read-only attribute to all mathfields
        mathfields.forEach((mathfield) => {
            mathfield.setAttribute('read-only', 'true');
        });
    }

    function onDocumentLoadSuccess() {

    }

    const documentRender = () => {
        // Use devicePixelRatio to render crisp PDFs on high-DPI displays (Windows scaling, Retina, etc.)
        // Minimum scale of 2 ensures crisp rendering even at DPR 1
        const pixelRatio = Math.max(window.devicePixelRatio || 1, 2);

        return (
            <Document
                file={pdfUrl}
                options={pdfOptions}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadProgress={({ loaded, total }) => {
                    if (total > 0) setPdfLoadProgress(Math.round((loaded / total) * 100));
                }}
                onContextMenu={(e) => e.preventDefault()}
                className="pdf-container"
                loading={
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={4}>
                        <CircularProgress color="inherit" size={40} />
                        {pdfLoadProgress > 0 && pdfLoadProgress < 100 && (
                            <Box width="60%">
                                <LinearProgress variant="determinate" value={pdfLoadProgress} />
                                <Typography variant="caption" textAlign="center" display="block" mt={0.5}>
                                    Loading PDF... {pdfLoadProgress}%
                                </Typography>
                            </Box>
                        )}
                    </Box>
                }
            >
                <div>
                    <Page
                        key={`page_${selectedPage}`}
                        pageNumber={selectedPage}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        scale={pixelRatio}
                        onRenderSuccess={() => setLoading(false)}
                        loading={
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                <CircularProgress color="inherit" size={40} />
                            </Box>
                        }
                    />
                </div>
            </Document>
        );
    };

    const handlePageChange = (e, upOrDown) => {
        e.preventDefault();
        const currentIndex = availablePages.indexOf(selectedPage);
        if (upOrDown === 1 && currentIndex < availablePages.length - 1) {
            setLoading(true);
            setSelectedPage(availablePages[currentIndex + 1]);
        } else if (upOrDown === 0 && currentIndex > 0) {
            setLoading(true);
            setSelectedPage(availablePages[currentIndex - 1]);
        }
    };

    // Helper to format page ranges for display
    const formatPageRanges = (pagesArr) => {
        if (!pagesArr || pagesArr.length === 0) return '';
        const sorted = [...pagesArr].sort((a, b) => a - b);
        const ranges = [];
        let start = sorted[0];
        let end = sorted[0];

        for (let i = 1; i <= sorted.length; i++) {
            if (i < sorted.length && sorted[i] === end + 1) {
                end = sorted[i];
            } else {
                ranges.push(start === end ? `${start}` : `${start}-${end}`);
                if (i < sorted.length) {
                    start = sorted[i];
                    end = sorted[i];
                }
            }
        }
        return ranges.join(', ');
    };

    const canGoPrevious = availablePages.indexOf(selectedPage) > 0;
    const canGoNext = availablePages.indexOf(selectedPage) < availablePages.length - 1;

    // const questionTypes = [
    //     { label: "Multiple Choice", value: "multiple_choice" },
    //     { label: "Short Answer", value: "short_answer" },
    //     { label: "Long Answer", value: "long_answer" },
    // ];

    const handleMouseOver = () => {
        if (userRole === 'student') {
            return;
        }

        const tooltip = document.querySelector('.tooltip');
        tooltip.style.display = 'flex';
    };

    const handleMouseLeave = () => {
        const tooltip = document.querySelector('.tooltip');
        tooltip.style.display = 'none';
    }

    const handleSaveLater = async (e, fromSubmitFunc) => {
        if (e && e.preventDefault) e.preventDefault();
        if (userRole !== 'student') {
            return false;
        }

        // A manual save supersedes any pending auto-save: cancel the debounce and wait
        // for any in-flight autosave to settle so it can't race this write or land
        // after a subsequent submit flips is_submitted.
        cancelAutoSaveTimer();
        if (savingPromiseRef.current) {
            try { await savingPromiseRef.current; } catch (_) { /* handled by the autosave path */ }
        }

        // Snapshot the values we're about to persist so that on success we only clear
        // the dirty flags for answers that were NOT edited again during the save.
        const savedSnapshot = {};
        dirtyRef.current.forEach((id) => {
            savedSnapshot[id] = JSON.stringify(hwAnswersRef.current[id]?.answer ?? null);
        });

        const ok = await persistAnswers({
            questionIds: null,      // save everything on an explicit save/submit
            includeFiles: true,
            feedback: !fromSubmitFunc, // submit shows its own backdrop/snackbar
            autosave: false,
        });

        if (ok) {
            retryCountRef.current = 0;
            Object.keys(savedSnapshot).forEach((id) => {
                const current = JSON.stringify(hwAnswersRef.current[id]?.answer ?? null);
                if (current === savedSnapshot[id]) dirtyRef.current.delete(id);
            });
            // Edits that arrived mid-save stay dirty; keep autosaving them (but not on
            // the submit path, which is finalizing).
            if (dirtyRef.current.size > 0 && !fromSubmitFunc) {
                setSaveStatus('unsaved');
                scheduleRef.current();
            } else {
                setSaveStatus('saved');
            }
        }
        return ok;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // do homework save (handleSaveLater)
        // and then change is_submitted to true on hw_submissions table
        // finally, go back to the homework submissions page
        setBackdropOpen(true);

        // 1. save the answers
        const fromSubmitFunc = true;
        const isSuccessful = await handleSaveLater(e, fromSubmitFunc);
        if (!isSuccessful) {
            setBackdropOpen(false);
            handleDialogClose();
            return;
        }

        // 2. change is_submitted to true on hw_submissions table
        await Axios.put(`${process.env.REACT_APP_URL}/api/v1/courses/${course_id}/homeworks/${homework_id}/students/${userId}/hw-submissions/${submissionId}/hw-questions/submit`, {
            is_submitted: true,
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then(() => {
            setBackdropOpen(false);
            setSuccessAlertMessage('Your homework submitted successfully!');
            setSuccessAlertOpen(true);
            setTimeout(() => {
                window.location.href = `/classrooms/${course_id}/homework/${homework_id}/submissions`;
            }, 1000);
        }).catch((error) => {
            if (error.response?.status === 401) {
                handleLogout();
                return;
            }
            setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
            setErrorAlertOpen(true);
            setBackdropOpen(false);
        });
    };

    const handleAnswerChange = (e, hw_question_id) => {
        e.preventDefault();
        commitAnswer(hw_question_id, e.target.value);
    };
    
    const flexDirectionForAnswer = (answer_tool, question_type) => {
        if (answer_tool === 'tinymce' || question_type === 'file_upload' || question_type === 'long_answer') {
            return 'column';
        } else {
            return 'row';
        }
    };

    const alignItemsForAnswer = (answer_tool, question_type) => {
        if (answer_tool === 'tinymce' || question_type === 'file_upload' || question_type === 'long_answer') {
            return 'flex-start';
        } else {
            return 'center';
        }
    };

    // Toggle is_correct status for an answer (admin only)
    const handleToggleCorrect = async (hw_answer) => {
        try {
            setBackdropOpen(true);
            const response = await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/hw-answers/${hw_answer.id}/toggle-correct`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );

            // Update local state
            setHWAnswers((prev) => ({
                ...prev,
                [hw_answer.hw_question_id]: {
                    ...prev[hw_answer.hw_question_id],
                    is_correct: response.data.is_correct,
                },
            }));

            setSuccessAlertMessage('Answer correctness updated!');
            setSuccessAlertOpen(true);
        } catch (error) {
            console.error('Error toggling answer:', error);
            setErrorAlertMessage(error.response?.data?.error || 'Failed to update answer');
            setErrorAlertOpen(true);
        } finally {
            setBackdropOpen(false);
        }
    };

    // Create a non-mutating regrade preview for a single answer (admin only).
    const handleRegrade = async (hw_answer) => {
        try {
            setBackdropOpen(true);
            const response = await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/hw-answers/${hw_answer.id}/regrade`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            setRegradePreview(response.data);
        } catch (error) {
            console.error('Error previewing answer regrade:', error);
            setErrorAlertMessage(error.response?.data?.error || 'Failed to preview answer regrade');
            setErrorAlertOpen(true);
        } finally {
            setBackdropOpen(false);
        }
    };

    // Create a non-mutating regrade preview for the submission (admin only).
    const handleRegradeAll = async () => {
        try {
            setBackdropOpen(true);
            const response = await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/hw-answers/regrade-all/${submissionId}`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            setRegradePreview(response.data);
        } catch (error) {
            console.error('Error previewing submission regrade:', error);
            setErrorAlertMessage(error.response?.data?.error || 'Failed to preview submission regrade');
            setErrorAlertOpen(true);
        } finally {
            setBackdropOpen(false);
        }
    };

    const handleApplyRegrade = async () => {
        if (!regradePreview?.batch_id || !regradePreview?.input_hash) return;
        try {
            setApplyingRegrade(true);
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-answers/regrade-batches/${regradePreview.batch_id}/apply`,
                { input_hash: regradePreview.input_hash },
                { headers: { Authorization: getAuthHeader() } }
            );

            const appliedIds = new Set((response.data.applied || []).map((item) => item.hw_answer_id));
            setHWAnswers((prev) => Object.fromEntries(
                Object.entries(prev).map(([questionId, answer]) => [
                    questionId,
                    answer && appliedIds.has(answer.id)
                        ? { ...answer, is_correct: true }
                        : answer,
                ])
            ));
            setRegradePreview(null);
            setSuccessAlertMessage(
                response.data.applied_count === 1
                    ? 'Applied 1 reviewed grading upgrade.'
                    : `Applied ${response.data.applied_count || 0} reviewed grading upgrades.`
            );
            setSuccessAlertOpen(true);
        } catch (error) {
            console.error('Error applying regrade preview:', error);
            setErrorAlertMessage(
                error.response?.data?.error
                || 'The preview could not be applied. Create a new preview and try again.'
            );
            setErrorAlertOpen(true);
        } finally {
            setApplyingRegrade(false);
        }
    };

    const handleOpenReviewQueue = async () => {
        setReviewQueueOpen(true);
        setLoadingReviewQueue(true);
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/hw-answers/review-queue`,
                {
                    params: { submission_id: submissionId },
                    headers: { Authorization: getAuthHeader() },
                }
            );
            setReviewQueueItems(response.data.items || []);
        } catch (error) {
            console.error('Error loading homework review queue:', error);
            setErrorAlertMessage(error.response?.data?.error || 'Failed to load the grading review queue');
            setErrorAlertOpen(true);
            setReviewQueueOpen(false);
        } finally {
            setLoadingReviewQueue(false);
        }
    };

    const handleResolveReview = async (item, isCorrect) => {
        try {
            setResolvingReviewAnswerId(item.hw_answer_id);
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-answers/${item.hw_answer_id}/resolve-review`,
                { is_correct: isCorrect },
                { headers: { Authorization: getAuthHeader() } }
            );
            setHWAnswers((prev) => ({
                ...prev,
                [item.hw_question_id]: {
                    ...prev[item.hw_question_id],
                    is_correct: response.data.is_correct,
                },
            }));
            setReviewQueueItems((prev) => prev.filter(
                queueItem => queueItem.hw_answer_id !== item.hw_answer_id
            ));
            setSuccessAlertMessage(
                `Answer marked ${response.data.is_correct ? 'Correct' : 'Incorrect'}.`
            );
            setSuccessAlertOpen(true);
        } catch (error) {
            console.error('Error resolving homework review:', error);
            setErrorAlertMessage(error.response?.data?.error || 'Failed to resolve the grading review');
            setErrorAlertOpen(true);
        } finally {
            setResolvingReviewAnswerId(null);
        }
    };

    const handleResult = (hw_answer, type, answer_tool, grading_type) => {
        // Binary AI-graded FRQ (apush_frq, middle_science_frq) — show correct/incorrect
        if (type === 'long_answer' && BINARY_GRADING_TYPES.includes(grading_type)) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {hw_answer.is_correct !== null ? (
                            <img
                                src={hw_answer.is_correct ? '/imgs/check-icon.png' : '/imgs/x-icon-final.png'}
                                alt={hw_answer.is_correct ? 'Correct' : 'Incorrect'}
                                style={{ width: '30px' }}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">Grading in progress...</Typography>
                        )}
                        {canManageGrades && hw_answer.is_correct !== null && (
                            <>
                                <Tooltip title="Toggle correct/incorrect">
                                    <IconButton size="small" onClick={() => handleToggleCorrect(hw_answer)} sx={{ color: colors.grey[300] }}>
                                        <SwapHorizIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Re-grade answer">
                                    <IconButton size="small" onClick={() => handleRegrade(hw_answer)} sx={{ color: colors.grey[300] }}>
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                    {/* Admin/Teacher: show AI reasoning */}
                    {!showAsStudent && hw_answer.feedback_json && (
                        <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: colors.primary[750], border: '1px solid lightgrey', mt: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                AI Reasoning (Score: {hw_answer.feedback_json.total_score})
                            </Typography>
                            {['level_1', 'level_2', 'level_3'].map((level, i) => (
                                <Box key={level} sx={{ mb: i < 2 ? 1 : 0 }}>
                                    <Typography variant="body2" fontWeight="bold">
                                        Level {i + 1} (Score: {hw_answer.feedback_json[`${level}_score`]})
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {hw_answer.feedback_json[`${level}_reasoning`]}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            );
        }

        if (type === 'multiple_choice' || type === 'short_answer') {
            // Skip if answer_tool handles its own display differently (but we want consistent badge)
            // Table and mapping now use this same badge for consistency
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
                    <img
                        src={hw_answer.is_correct ? '/imgs/check-icon.png' : '/imgs/x-icon-final.png'}
                        alt={hw_answer.is_correct ? 'Correct' : 'Incorrect'}
                        style={{ width: '30px' }}
                    />
                    {canManageGrades && (
                        <>
                            <Tooltip title="Toggle correct/incorrect">
                                <IconButton
                                    size="small"
                                    onClick={() => handleToggleCorrect(hw_answer)}
                                    sx={{ color: colors.grey[300] }}
                                >
                                    <SwapHorizIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Re-grade answer">
                                <IconButton
                                    size="small"
                                    onClick={() => handleRegrade(hw_answer)}
                                    sx={{ color: colors.grey[300] }}
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            );
        } else if (type === 'file_upload' || type === 'long_answer') {
            return (
                <Box
                    sx={{
                        padding: '10px',
                        borderRadius: '5px',
                        backgroundColor: colors.primary[750],
                        border: '1px solid lightgrey',
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 'bold',
                        }}
                    >
                        {hw_answer?.given_score && hw_answer?.total_score ? `Score: ${hw_answer?.given_score}%` : 'Not graded yet'}
                    </Typography>
                    {type !== 'long_answer' && hw_answer?.feedback !== null && hw_answer?.feedback !== '' && (
                        <Box
                            sx={{
                                marginTop: '20px',
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 'bold',
                                    marginBottom: '10px',
                                }}
                            >
                                Feedback:
                            </Typography>
                            {hw_answer?.feedback.split('\n').map((line, index) => {
                                return (
                                    <Typography key={index} variant="h6">
                                        {line === '' ? <br /> : line}
                                    </Typography>
                                );
                            })}
                        </Box>
                    )}
                    {/* View Feedback button for long_answer with feedback_json */}
                    {type === 'long_answer' && hw_answer?.feedback_json && Object.keys(hw_answer.feedback_json).length > 0 && (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ mt: 2 }}
                            onClick={() => navigate(
                                `/classrooms/${course_id}/homework/${homework_id}/essay-feedback/${hw_answer.id}`
                            )}
                        >
                            View Feedback
                        </Button>
                    )}
                    {!showAsStudent && (
                        <GradeModal
                            hw_answer={hw_answer}
                            setErrorAlertMessage={setErrorAlertMessage}
                            setErrorAlertOpen={setErrorAlertOpen}
                            setSuccessAlertOpen={setSuccessAlertOpen}
                            setSuccessAlertMessage={setSuccessAlertMessage}
                            setBackdropOpen={setBackdropOpen}
                            setUpdatedHWAnswers={setUpdatedHWAnswers}
                        />
                    )}
                </Box>
            );
        }
    }

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: "1rem",
                    gap: "1rem",
                    justifyContent: "center",
                    width: "100%",
                    position: "relative",
                }}
            >
                {/* Page Navigator */}
                {canGoPrevious && (
                    <NavigateBeforeIcon fontSize='large' sx={{cursor: 'pointer'}} onClick={(e) => handlePageChange(e, 0)} />
                )}
                <Typography variant="h6" color="textSecondary">
                    Page {selectedPage} ({formatPageRanges(availablePages)})
                </Typography>
                {canGoNext && (
                    <NavigateNextIcon fontSize='large' sx={{cursor: 'pointer'}} onClick={(e) => handlePageChange(e, 1)} />
                )}
                {!isSubmitted && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "0",
                            right: "0",
                            display: "flex",
                            gap: "10px"
                        }}
                    >
                        {isStudent && saveStatus !== 'idle' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                {saveStatus === 'saving' && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        icon={<CircularProgress size={12} thickness={6} sx={{ color: 'inherit' }} />}
                                        label="Saving…"
                                        sx={{ color: colors.grey[300], borderColor: colors.grey[700] }}
                                    />
                                )}
                                {saveStatus === 'unsaved' && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label="Unsaved changes…"
                                        sx={{ color: colors.grey[300], borderColor: colors.grey[700] }}
                                    />
                                )}
                                {saveStatus === 'saved' && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label="All changes saved"
                                        sx={{ color: colors.greenAccent[400], borderColor: colors.greenAccent[400] }}
                                    />
                                )}
                                {saveStatus === 'error' && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label="Couldn't save — will retry"
                                        sx={{ color: colors.redAccent[400], borderColor: colors.redAccent[400] }}
                                    />
                                )}
                                {saveStatus === 'offline' && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label="Offline — will save when reconnected"
                                        sx={{ color: colors.yellowAccent ? colors.yellowAccent[500] : '#ffc107', borderColor: colors.grey[700] }}
                                    />
                                )}
                            </Box>
                        )}
                        <Box
                            className="tooltip"
                            sx={{
                                display: "none",
                                gap: "10px",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "absolute",
                                top: "50px",
                                right: "0",
                                backgroundColor: colors.primary[600],
                                padding: "10px",
                                borderRadius: "5px",
                                border: "1px solid #000",
                                zIndex: "100",
                                textAlign: "center",
                            }}
                        >
                            <Typography variant="h6">
                                Only students can submit homework
                            </Typography>
                        </Box>
                        <Button
                            type='button' 
                            variant="contained" 
                            color="info"
                            sx={{
                                minWidth: "8rem",
                                cursor: userRole === 'student' ? 'pointer' : 'not-allowed',
                            }}
                            onClick={(e) => handleSaveLater(e, false)}
                            onMouseOver={handleMouseOver}
                            onMouseLeave={handleMouseLeave}
                        >
                            Save for later
                        </Button>
                        <Button 
                            type='button' 
                            variant="contained" 
                            color="secondary"
                            sx={{
                                minWidth: "4rem",
                                cursor: userRole === 'student' ? 'pointer' : 'not-allowed',
                            }}
                            onClick={handleClickDialogOpen}
                            onMouseOver={handleMouseOver}
                            onMouseLeave={handleMouseLeave}
                        >
                            Submit
                        </Button>
                        <Dialog
                            open={submitDialogOpen}
                            onClose={handleDialogClose}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle 
                                id="alert-dialog-title"
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '22px',
                                    minWidth: '500px',
                                }}
                            >
                                {"Are you sure you want to submit?"}
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description" sx={{ fontSize: '16px' }}>
                                    You cannot change your answers after submitting and 
                                    there will not be a second attempt for this homework.
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions sx={{ padding: '20px' }}>
                                <Button onClick={handleDialogClose} variant="contained" color='error'>Cancel</Button>
                                <Button 
                                    onClick={handleSubmit} 
                                    autoFocus
                                    variant="contained"
                                    color="secondary"
                                >
                                    Submit
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Box>
                )}
                {/* Re-grade All button for admins when submitted */}
                {isSubmitted && canManageGrades && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "0",
                            right: "0",
                            display: 'flex',
                            gap: 1,
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<FactCheckIcon />}
                            onClick={handleOpenReviewQueue}
                        >
                            Review Queue
                        </Button>
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={handleRegradeAll}
                        >
                            Re-grade All
                        </Button>
                    </Box>
                )}
                <RegradePreviewDialog
                    open={Boolean(regradePreview)}
                    preview={regradePreview}
                    applying={applyingRegrade}
                    onClose={() => setRegradePreview(null)}
                    onApply={handleApplyRegrade}
                />
                <HomeworkReviewQueueDialog
                    open={reviewQueueOpen}
                    loading={loadingReviewQueue}
                    items={reviewQueueItems}
                    resolvingAnswerId={resolvingReviewAnswerId}
                    onClose={() => setReviewQueueOpen(false)}
                    onResolve={handleResolveReview}
                />
            </Box>
            <Box
                display="flex"
                width="100%"
            >
                <Box
                    flex={8}
                    width="100%"
                    sx={{
                        position: 'sticky',
                        top: 20,
                        alignSelf: 'flex-start',
                    }}
                >
                    {documentRender()}
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box
                    flex={7}
                    width="100%"
                    p={5}
                    display='flex'
                    flexDirection='column'
                    gap={3}
                >
                    {/* hw questions */}
                    {hwQuestions && hwQuestions.length > 0 ? (<>
                        {hwQuestions.map((question, index) => {
                            if (question.page !== selectedPage)
                                return null;

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '20px',
                                        paddingBottom: '20px',
                                        borderBottom: index !== hwQuestions.length - 1 ? '1px solid lightgrey' : 'none',
                                    }}
                                >
                                    {/* Question Label */}
                                    <Typography variant="h4" sx={{'fontWeight': 'bold'}}>
                                        {question.question_label} {question.question_type === 'file_upload' && !isSubmitted ? 'Upload an image file for this question!' : null}
                                    </Typography>

                                    {/* Passage (if exists) */}
                                    {question.passage && (
                                        <Box
                                            sx={{
                                                padding: '15px',
                                                borderRadius: '8px',
                                                backgroundColor: colors.blueAccent[800],
                                                border: `1px solid ${colors.blueAccent[600]}`,
                                            }}
                                        >
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: colors.blueAccent[300] }}>
                                                Passage / Context:
                                            </Typography>
                                            <QuestionRender content={question.passage} />
                                        </Box>
                                    )}

                                    {/* Question Content */}
                                    {question.content && (
                                        <Box sx={{ mb: 1 }}>
                                            <QuestionRender content={question.content} />
                                        </Box>
                                    )}

                                    {/* Answer Input Fields */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: flexDirectionForAnswer(question.answer_tool, question.question_type),
                                            alignItems: alignItemsForAnswer(question.answer_tool, question.question_type),
                                            gap: '20px',
                                        }}
                                    >
                                        {question.question_type === 'multiple_choice' ? (
                                            <FormControl
                                                disabled={isSubmitted}
                                                fullWidth
                                            >
                                                <RadioGroup
                                                    aria-labelledby="demo-controlled-radio-buttons-group"
                                                    name="controlled-radio-buttons-group"
                                                    value={hwAnswers[question.id]?.answer !== undefined ? String(hwAnswers[question.id]?.answer) : ''}
                                                    onChange={(e) => handleAnswerChange(e, question.id)}
                                                >
                                                    {(Array.isArray(question.options) ? question.options : question.options?.split('|') || []).map((option, optionIndex) => {
                                                        return (
                                                            <FormControlLabel
                                                                key={optionIndex}
                                                                value={String(optionIndex)}
                                                                control={<Radio />}
                                                                label={<QuestionRender content={option} />}
                                                            />
                                                        );
                                                    })}
                                                </RadioGroup>
                                            </FormControl>
                                        ) : question.question_type === 'short_answer' && (!question.answer_tool || question.answer_tool === 'textfield') ? (
                                            <TextField
                                                disabled={isSubmitted}
                                                variant="outlined"
                                                type="text"
                                                label={userRole === 'student' ? 'Your Answer' : 'Student Answer'}
                                                value={hwAnswers[question.id]?.answer || ''}
                                                onChange={(e) => handleAnswerChange(e, question.id)}
                                                sx={{ maxWidth: '200px' }}
                                            />
                                        ) : question.question_type === 'short_answer' && question.answer_tool === 'mathlive' ? (
                                            question.answer_config?.allowTextInput === true ? (
                                                (() => {
                                                    const parsed = parseMathliveAnswer(hwAnswers[question.id]?.answer);
                                                    const currentType = parsed?.type || MATHLIVE_ANSWER_TYPES.MATH;
                                                    const currentValue = parsed?.value || '';
                                                    const isTextType = currentType === MATHLIVE_ANSWER_TYPES.TEXT;
                                                    const isMathType = !isTextType;
                                                    const hasValue = !!String(currentValue).trim();
                                                    const mathReadOnly = isSubmitted || (hasValue && isTextType);
                                                    const textDisabled = isSubmitted || (hasValue && isMathType);
                                                    const writeAnswer = (type, value) => {
                                                        commitAnswer(question.id, stringifyMathliveAnswer({ type, value }));
                                                    };
                                                    const displayLabel = userRole === 'student' ? 'Your Answer' : 'Student Answer';
                                                    if (isSubmitted) {
                                                        return (
                                                            <p>{displayLabel}: {isTextType
                                                                ? <span>{currentValue}</span>
                                                                : <math-field read-only style={{ display: 'inline-block', verticalAlign: 'middle', minWidth: '50px', border: 'none' }}>{currentValue}</math-field>
                                                            }</p>
                                                        );
                                                    }
                                                    return (
                                                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <math-field
                                                                id={`mathfield-${question.id}`}
                                                                onInput={evt => {
                                                                    writeAnswer(MATHLIVE_ANSWER_TYPES.MATH, evt.target.value || '');
                                                                }}
                                                                style={{ width: "200px", minWidth: "200px", minHeight: "40px", opacity: mathReadOnly && !isSubmitted ? 0.5 : 1 }}
                                                                read-only={mathReadOnly ? true : undefined}
                                                                ref={(el) => {
                                                                    const expected = isMathType ? currentValue : '';
                                                                    if (el && el.getValue() !== expected) {
                                                                        el.setValue(expected);
                                                                    }
                                                                }}
                                                            >
                                                            </math-field>
                                                            <Typography variant="caption" sx={{ mx: 1 }}>or</Typography>
                                                            <TextField
                                                                disabled={textDisabled}
                                                                variant="outlined"
                                                                type="text"
                                                                size="small"
                                                                label="Text answer"
                                                                value={isTextType ? currentValue : ''}
                                                                onChange={(e) => writeAnswer(MATHLIVE_ANSWER_TYPES.TEXT, e.target.value)}
                                                                sx={{ maxWidth: '200px' }}
                                                            />
                                                        </Box>
                                                    );
                                                })()
                                            ) : (
                                                <>
                                                    <math-field
                                                        id={`mathfield-${question.id}`}
                                                        onInput={evt => {
                                                            handleAnswerChange(evt, question.id);
                                                        }}
                                                        style={{ width: "200px", minWidth: "200px", minHeight: "40px" }}
                                                        read-only={isSubmitted ? true : undefined}
                                                        ref={(el) => {
                                                            // Pre-populate MathLive with saved answer
                                                            if (el && hwAnswers[question.id]?.answer && el.getValue() !== hwAnswers[question.id].answer) {
                                                                el.setValue(hwAnswers[question.id].answer);
                                                            }
                                                        }}
                                                    >
                                                    </math-field>
                                                    <p>{userRole === 'student' ? 'Your Answer' : 'Student Answer'}: <math-field read-only style={{ display: 'inline-block', verticalAlign: 'middle', minWidth: '50px', border: 'none' }}>{hwAnswers[question.id]?.answer || ''}</math-field></p>
                                                </>
                                            )
                                        ) : (question.question_type === 'short_answer' || question.question_type === 'long_answer') && question.answer_tool === 'tinymce' ? (
                                            <Box width={'100%'}>
                                                {isSubmitted ? (
                                                    question.question_type === 'long_answer' ? (
                                                        <CollapsibleAnswer
                                                            content={hwAnswers[question.id]?.answer}
                                                            isHtml={true}
                                                            colors={colors}
                                                        />
                                                    ) : (
                                                        <div dangerouslySetInnerHTML={{__html: hwAnswers[question.id]?.answer || ''}} />
                                                    )
                                                ) : (
                                                    <EditorTiny
                                                        value={hwAnswers[question.id]?.answer || ''}
                                                        setHWAnswers={(updater) => {
                                                            const prevAnswers = hwAnswersRef.current;
                                                            setHWAnswers(updater);
                                                            // TinyMCE fires onEditorChange on programmatic
                                                            // repopulation too (e.g. after refresh-on-refocus);
                                                            // only mark dirty when the answer actually changed.
                                                            const next = typeof updater === 'function' ? updater(prevAnswers) : updater;
                                                            if (next?.[question.id]?.answer !== prevAnswers?.[question.id]?.answer) {
                                                                markDirty(question.id);
                                                            }
                                                        }}
                                                        hw_question_id={question.id}
                                                    />
                                                )}
                                            </Box>
                                        ) : question.question_type === 'long_answer' && question.answer_tool === 'textfield' ? (
                                            isSubmitted ? (
                                                <CollapsibleAnswer
                                                    content={hwAnswers[question.id]?.answer}
                                                    isHtml={false}
                                                    colors={colors}
                                                />
                                            ) : (
                                                <TextField
                                                    multiline
                                                    variant="outlined"
                                                    type="text"
                                                    label={userRole === 'student' ? 'Your Answer' : 'Student Answer'}
                                                    value={hwAnswers[question.id]?.answer || ''}
                                                    onChange={(e) => handleAnswerChange(e, question.id)}
                                                    sx={{ width: '100%' }}
                                                />
                                            )
                                        ) : question.question_type === 'long_answer' && question.answer_tool === 'mathlive' ? (
                                            <>
                                                <math-field
                                                    id={`mathfield-${question.id}`}
                                                    onInput={evt => {
                                                        handleAnswerChange(evt, question.id);
                                                    }}
                                                    style={{ width: "100%", minHeight: "40px" }}
                                                    read-only={isSubmitted ? true : undefined}
                                                    ref={(el) => {
                                                        // Pre-populate MathLive with saved answer
                                                        if (el && hwAnswers[question.id]?.answer && el.getValue() !== hwAnswers[question.id].answer) {
                                                            el.setValue(hwAnswers[question.id].answer);
                                                        }
                                                    }}
                                                >
                                                </math-field>
                                                <p>{userRole === 'student' ? 'Your Answer' : 'Student Answer'}: <math-field read-only style={{ display: 'inline-block', verticalAlign: 'middle', minWidth: '50px', border: 'none' }}>{hwAnswers[question.id]?.answer || ''}</math-field></p>
                                            </>
                                        ) : question.question_type === 'short_answer' && question.answer_tool === 'coordinate' ? (
                                            isSubmitted ? (
                                                <Box sx={{ width: '100%' }}>
                                                    <CoordinateGridReadOnly
                                                        config={question.answer_config || defaultCoordinateConfig}
                                                        studentAnswer={parseAnswerData(hwAnswers[question.id]?.answer, { elements: [] })}
                                                        correctAnswer={parseAnswerData(question.answer, null)}
                                                        isCorrect={hwAnswers[question.id]?.is_correct}
                                                        showCorrect={true}
                                                    />

                                                    {/* Answer Validation Logic Summary - shown after submission (full mode) */}
                                                    <AnswerLogicSummary
                                                        elements={parseAnswerData(question.answer, { elements: [] })?.elements || []}
                                                        answerConfig={question.answer_config}
                                                        compact={true}
                                                        colors={colors}
                                                        mode="full"
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{ width: '100%' }}>
                                                    <CoordinateGridStudent
                                                        config={{
                                                            ...(question.answer_config || defaultCoordinateConfig),
                                                            allowedModes: getUnionAllowedModes(question)
                                                        }}
                                                        value={parseAnswerData(hwAnswers[question.id]?.answer, { elements: [] })}
                                                        onChange={(value) => {
                                                            commitAnswer(question.id, [JSON.stringify(value)]);
                                                        }}
                                                    />

                                                    {/* Answer Validation Logic Summary - shown during solving (preview mode) */}
                                                    <AnswerLogicSummary
                                                        elements={[]}
                                                        answerConfig={question.answer_config}
                                                        allowedModes={getUnionAllowedModes(question)}
                                                        compact={true}
                                                        colors={colors}
                                                        mode="preview"
                                                    />
                                                </Box>
                                            )
                                        ) : question.question_type === 'short_answer' && question.answer_tool === 'table' ? (
                                            isSubmitted ? (
                                                <Box sx={{ width: '100%' }}>
                                                    <TableReadOnly
                                                        config={question.answer_config || defaultTableConfig}
                                                        studentAnswer={parseAnswerData(hwAnswers[question.id]?.answer, { rows: [] })}
                                                        correctAnswer={
                                                            parseAnswerData(question.answer, null) ||
                                                            extractTableCorrectAnswer(question.answer_config)
                                                        }
                                                        isCorrect={null}
                                                        showCorrect={true}
                                                    />

                                                    {/* Table Answer Validation Logic Summary - shown after submission (full mode) */}
                                                    <TableAnswerLogicSummary
                                                        config={question.answer_config || defaultTableConfig}
                                                        compact={true}
                                                        colors={colors}
                                                        mode="full"
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{ width: '100%' }}>
                                                    <TableStudent
                                                        config={question.answer_config || defaultTableConfig}
                                                        value={parseAnswerData(hwAnswers[question.id]?.answer, { rows: [] })}
                                                        onChange={(value) => {
                                                            commitAnswer(question.id, [JSON.stringify(value)]);
                                                        }}
                                                    />

                                                    {/* Table Answer Validation Logic Summary - shown during solving (preview mode) */}
                                                    <TableAnswerLogicSummary
                                                        config={question.answer_config || defaultTableConfig}
                                                        compact={true}
                                                        colors={colors}
                                                        mode="preview"
                                                    />
                                                </Box>
                                            )
                                        ) : question.question_type === 'short_answer' && question.answer_tool === 'numberline' ? (
                                            isSubmitted ? (
                                                <Box sx={{ width: '100%' }}>
                                                    <NumberLineReadOnly
                                                        config={question.answer_config || defaultNumberLineConfig}
                                                        studentAnswer={parseAnswerData(hwAnswers[question.id]?.answer, { elements: [] })}
                                                        correctAnswer={parseAnswerData(question.answer, null)}
                                                        isCorrect={hwAnswers[question.id]?.is_correct}
                                                        showCorrect={true}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{ width: '100%' }}>
                                                    <NumberLineStudent
                                                        config={{
                                                            ...(question.answer_config || defaultNumberLineConfig),
                                                            allowedModes: getUnionAllowedModesNumberLine(question)
                                                        }}
                                                        value={parseAnswerData(hwAnswers[question.id]?.answer, { elements: [] })}
                                                        onChange={(value) => {
                                                            commitAnswer(question.id, [JSON.stringify(value)]);
                                                        }}
                                                        question={question}
                                                    />
                                                </Box>
                                            )
                                        ) : question.question_type === 'short_answer' && question.answer_tool === 'mapping' ? (
                                            isSubmitted ? (
                                                <Box sx={{ width: '100%' }}>
                                                    <MappingReadOnly
                                                        config={question.answer_config || defaultMappingConfig}
                                                        studentAnswer={
                                                            // Handle both array and string formats
                                                            Array.isArray(hwAnswers[question.id]?.answer)
                                                                ? hwAnswers[question.id].answer[0] || ''
                                                                : hwAnswers[question.id]?.answer || ''
                                                        }
                                                        correctAnswer={
                                                            // Handle both array and string formats, with fallback to answer_config
                                                            (Array.isArray(question.answer)
                                                                ? question.answer[0]
                                                                : question.answer) ||
                                                            extractMappingCorrectAnswer(question.answer_config)
                                                        }
                                                        showCorrectAnswer={true}
                                                        showGradeBadge={false}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{ width: '100%' }}>
                                                    <MappingStudent
                                                        config={question.answer_config || defaultMappingConfig}
                                                        value={
                                                            // Handle both array and string formats
                                                            Array.isArray(hwAnswers[question.id]?.answer)
                                                                ? hwAnswers[question.id].answer[0] || ''
                                                                : hwAnswers[question.id]?.answer || ''
                                                        }
                                                        onChange={(value) => {
                                                            commitAnswer(question.id, [value]);
                                                        }}
                                                    />
                                                </Box>
                                            )
                                        ) : question.question_type === 'short_answer' && question.answer_tool === 'polynomialGraph' ? (
                                            isSubmitted ? (
                                                <Box sx={{ width: '100%' }}>
                                                    <PolynomialGraphReadOnly
                                                        config={question.answer_config || defaultPolynomialGraphConfig}
                                                        studentAnswer={parseAnswerData(hwAnswers[question.id]?.answer, {})}
                                                        correctAnswer={parseAnswerData(question.answer, { correctGraphIndex: 0 })}
                                                        isCorrect={hwAnswers[question.id]?.is_correct}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{ width: '100%' }}>
                                                    <PolynomialGraphStudent
                                                        config={question.answer_config || defaultPolynomialGraphConfig}
                                                        value={parseAnswerData(hwAnswers[question.id]?.answer, {})}
                                                        onChange={(value) => {
                                                            commitAnswer(question.id, [JSON.stringify(value)]);
                                                        }}
                                                    />
                                                </Box>
                                            )
                                        ) : question.question_type === 'short_answer' && question.answer_tool === 'rationalGraph' ? (
                                            isSubmitted ? (
                                                <Box sx={{ width: '100%' }}>
                                                    <RationalGraphReadOnly
                                                        config={question.answer_config || defaultRationalGraphConfig}
                                                        studentAnswer={parseAnswerData(hwAnswers[question.id]?.answer, {})}
                                                        correctAnswer={parseAnswerData(question.answer, { correctGraphIndex: 0 })}
                                                        isCorrect={hwAnswers[question.id]?.is_correct}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{ width: '100%' }}>
                                                    <RationalGraphStudent
                                                        config={question.answer_config || defaultRationalGraphConfig}
                                                        value={parseAnswerData(hwAnswers[question.id]?.answer, {})}
                                                        onChange={(value) => {
                                                            commitAnswer(question.id, [JSON.stringify(value)]);
                                                        }}
                                                    />
                                                </Box>
                                            )
                                        ) : question.question_type === 'short_answer' && question.answer_tool === 'trigGraph' ? (
                                            isSubmitted ? (
                                                <Box sx={{ width: '100%' }}>
                                                    <TrigGraphReadOnly
                                                        config={question.answer_config || defaultTrigGraphConfig}
                                                        studentAnswer={parseAnswerData(hwAnswers[question.id]?.answer, {})}
                                                        correctAnswer={parseAnswerData(question.answer, { correctGraphIndex: 0 })}
                                                        isCorrect={hwAnswers[question.id]?.is_correct}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{ width: '100%' }}>
                                                    <TrigGraphStudent
                                                        config={question.answer_config || defaultTrigGraphConfig}
                                                        value={parseAnswerData(hwAnswers[question.id]?.answer, {})}
                                                        onChange={(value) => {
                                                            commitAnswer(question.id, [JSON.stringify(value)]);
                                                        }}
                                                    />
                                                </Box>
                                            )
                                        ) : question.question_type === 'short_answer' && question.answer_tool === 'multiline' ? (
                                            isSubmitted ? (
                                                <Box sx={{ width: '100%' }}>
                                                    <MultilineReadOnly
                                                        config={question.answer_config || defaultMultilineConfig}
                                                        studentAnswer={parseAnswerData(hwAnswers[question.id]?.answer, { rows: [] })}
                                                        correctAnswer={parseAnswerData(question.answer, { rows: [] })}
                                                        isCorrect={hwAnswers[question.id]?.is_correct}
                                                        showCorrect={true}
                                                    />
                                                </Box>
                                            ) : (
                                                <Box sx={{ width: '100%' }}>
                                                    <MultilineStudent
                                                        config={question.answer_config || defaultMultilineConfig}
                                                        value={parseAnswerData(hwAnswers[question.id]?.answer, { rows: [] })}
                                                        onChange={(value) => {
                                                            commitAnswer(question.id, [JSON.stringify(value)]);
                                                        }}
                                                    />
                                                </Box>
                                            )
                                        ) : question.question_type === 'file_upload' ? (
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                sx={{ width: "100%", gap: "10px" }}
                                            >
                                                {isSubmitted ? (
                                                    <><Typography variant="h5" sx={{'fontWeight': 'bold'}}>
                                                        {hwAnswerFiles[question.id]?.prev_file?.file_url ? 'File Uploaded' : 'No file uploaded'}
                                                    </Typography>
                                                    <img 
                                                        src={hwAnswerFiles[question.id]?.prev_file?.file_url} 
                                                        alt={hwAnswerFiles[question.id]?.prev_file?.file_name} 
                                                        style={{ width: "400px", cursor: "pointer" }}
                                                        onClick={() => {
                                                            window.open(hwAnswerFiles[question.id]?.prev_file?.file_url, '_blank');
                                                        }}
                                                    /></>
                                                ) : (
                                                    <FileUpload
                                                        setHWAnswerFiles={setHWAnswerFiles}
                                                        hwAnswerFiles={hwAnswerFiles}
                                                        hw_question_id={question.id}
                                                    />
                                                )}
                                            </Box>
                                        ) : null}
                                        {isSubmitted && handleResult(hwAnswers[question.id], question.question_type, question.answer_tool, question.grading_type)}
                                    </Box>
                                    {(isSubmitted || !showAsStudent) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: '10px',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {(question.question_type !== 'file_upload' && question.question_type !== 'long_answer' && question.answer_tool !== 'tinymce' && question.answer_tool !== 'numberline') && (
                                                <Box
                                                    sx={{
                                                        padding: '10px',
                                                        borderRadius: '5px',
                                                        backgroundColor: colors.primary[750],
                                                        border: '1px solid lightgrey',
                                                    }}
                                                >
                                                    <Typography variant="h5" sx={{'fontWeight': 'bold', mb: 1}}>
                                                        Correct Answer{Array.isArray(question.answer) && question.answer.length > 1 ? 's' : ''}:
                                                    </Typography>
                                                    {question.answer_tool === 'coordinate' ? (
                                                        // For coordinate type: render visual grid
                                                        formatCoordinateAnswerDisplay(question.answer, question.answer_config, colors)
                                                    ) : question.answer_tool === 'table' ? (
                                                        // For table type: render table with correct values
                                                        <TableReadOnly
                                                            config={question.answer_config || defaultTableConfig}
                                                            studentAnswer={
                                                                parseAnswerData(question.answer, null) ||
                                                                extractTableCorrectAnswer(question.answer_config)
                                                            }
                                                            correctAnswer={null}
                                                            isCorrect={null}
                                                            showCorrect={false}
                                                        />
                                                    ) : question.answer_tool === 'mapping' ? (
                                                        // For mapping type: render mapping with correct connections
                                                        <MappingReadOnly
                                                            config={question.answer_config || defaultMappingConfig}
                                                            studentAnswer={
                                                                (Array.isArray(question.answer)
                                                                    ? question.answer[0]
                                                                    : question.answer) ||
                                                                extractMappingCorrectAnswer(question.answer_config)
                                                            }
                                                            correctAnswer={
                                                                (Array.isArray(question.answer)
                                                                    ? question.answer[0]
                                                                    : question.answer) ||
                                                                extractMappingCorrectAnswer(question.answer_config)
                                                            }
                                                            showCorrectAnswer={false}
                                                            showGradeBadge={false}
                                                        />
                                                    ) : question.answer_tool === 'polynomialGraph' ? (
                                                        // Polynomial graph answer - show visual graph
                                                        (() => {
                                                            let answerData = { correctGraphIndex: 0 };
                                                            try {
                                                                if (question.answer && question.answer[0]) {
                                                                    answerData = typeof question.answer[0] === 'string'
                                                                        ? JSON.parse(question.answer[0]) : question.answer[0];
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing polynomial graph answer:', e);
                                                            }
                                                            return (
                                                                <PolynomialGraphReadOnly
                                                                    config={question.answer_config || defaultPolynomialGraphConfig}
                                                                    studentAnswer={answerData}
                                                                    correctAnswer={answerData}
                                                                />
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'rationalGraph' ? (
                                                        // Rational graph answer - show visual graph
                                                        (() => {
                                                            let answerData = { correctGraphIndex: 0 };
                                                            try {
                                                                if (question.answer && question.answer[0]) {
                                                                    answerData = typeof question.answer[0] === 'string'
                                                                        ? JSON.parse(question.answer[0]) : question.answer[0];
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing rational graph answer:', e);
                                                            }
                                                            return (
                                                                <RationalGraphReadOnly
                                                                    config={question.answer_config || defaultRationalGraphConfig}
                                                                    studentAnswer={answerData}
                                                                    correctAnswer={answerData}
                                                                />
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'trigGraph' ? (
                                                        // Trig graph answer - show visual graph
                                                        (() => {
                                                            let answerData = { correctGraphIndex: 0 };
                                                            try {
                                                                if (question.answer && question.answer[0]) {
                                                                    answerData = typeof question.answer[0] === 'string'
                                                                        ? JSON.parse(question.answer[0]) : question.answer[0];
                                                                }
                                                            } catch (e) {
                                                                console.error('Error parsing trig graph answer:', e);
                                                            }
                                                            return (
                                                                <TrigGraphReadOnly
                                                                    config={question.answer_config || defaultTrigGraphConfig}
                                                                    studentAnswer={answerData}
                                                                    correctAnswer={answerData}
                                                                />
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'multiline' ? (
                                                        (() => {
                                                            let answerData = { rows: [] };
                                                            try {
                                                                if (question.answer && question.answer[0]) {
                                                                    answerData = typeof question.answer[0] === 'string'
                                                                        ? JSON.parse(question.answer[0]) : question.answer[0];
                                                                }
                                                            } catch (e) {
                                                                // Malformed answer JSON — fall back to empty rows
                                                            }
                                                            return (
                                                                <MultilineReadOnly
                                                                    config={question.answer_config || defaultMultilineConfig}
                                                                    studentAnswer={answerData}
                                                                    correctAnswer={answerData}
                                                                    showCorrect={false}
                                                                />
                                                            );
                                                        })()
                                                    ) : question.answer_tool === 'mathlive' ? (
                                                        (() => {
                                                            const answers = Array.isArray(question.answer)
                                                                ? question.answer
                                                                : [question.answer];
                                                            return answers.map((rawAns, idx) => {
                                                                const parsed = parseMathliveAnswer(rawAns);
                                                                if (parsed?.type === MATHLIVE_ANSWER_TYPES.TEXT) {
                                                                    return (
                                                                        <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0 }}>
                                                                            <Typography variant="body2">{parsed.value}</Typography>
                                                                        </Box>
                                                                    );
                                                                }
                                                                return (
                                                                    <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0 }}>
                                                                        <QuestionRender content={String(parsed?.value ?? rawAns ?? '')} />
                                                                    </Box>
                                                                );
                                                            });
                                                        })()
                                                    ) : question.question_type === 'multiple_choice' ? (
                                                        // For MC: support both index-based (new) and text-based (old) answers
                                                        (() => {
                                                            const answers = Array.isArray(question.answer) ? question.answer : [question.answer];
                                                            const options = Array.isArray(question.options) ? question.options : question.options?.split('|') || [];
                                                            // Detect if answers are indices (numbers) or text (old format)
                                                            // Uses robust logic: check question type, handle number types, validate range
                                                            const isIndexBased = question.question_type === 'multiple_choice' &&
                                                                answers.length > 0 &&
                                                                answers.every(a => {
                                                                    const num = typeof a === 'number' ? a : parseInt(a, 10);
                                                                    return !isNaN(num) && num >= 0 && num < options.length;
                                                                });

                                                            return answers.map((ans, idx) => {
                                                                const displayText = isIndexBased
                                                                    ? (options[parseInt(ans, 10)] || `Option ${parseInt(ans, 10) + 1}`)
                                                                    : ans; // Old format: ans is already the text
                                                                return (
                                                                    <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0 }}>
                                                                        <QuestionRender content={displayText} />
                                                                    </Box>
                                                                );
                                                            });
                                                        })()
                                                    ) : (
                                                        // For other types: answers are literal strings
                                                        Array.isArray(question.answer) ? (
                                                            question.answer.map((ans, idx) => (
                                                                <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0 }}>
                                                                    <QuestionRender content={ans} />
                                                                </Box>
                                                            ))
                                                        ) : (
                                                            <QuestionRender content={question.answer} />
                                                        )
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </>) : (
                        <Typography variant="h5" sx={{'fontWeight': 'bold'}}>
                            No questions found in this page
                        </Typography>
                    )}
                </Box>
            </Box>
        </>
    )
}

export default TextbookViewer
